<template>
  <span
    class="luce-mark"
    :class="{ 'is-static': !animated }"
    :style="{ '--taille': `${size}px` }"
    aria-hidden="true"
  >
    <svg viewBox="0 0 64 64">
      <defs v-if="source">
        <radialGradient
          :id="gradientId"
          cx="38%"
          cy="34%"
          r="72%"
        >
          <stop
            offset="0%"
            stop-color="#ffffff"
          />
          <stop
            offset="30%"
            stop-color="#9ff0e4"
          />
          <stop
            offset="100%"
            stop-color="#3fb0a8"
          />
        </radialGradient>
      </defs>

      <rect
        width="64"
        height="64"
        :rx="source ? 15 : 13"
        :fill="tuile"
      />
      <g
        transform="translate(1.55 4.7) scale(1.05)"
        fill="#e8f1fa"
      >
        <path d="M 30.6 5.2 L 28.8 4.0 L 28.1 4.0 L 25.5 7.7 L 23.1 11.6 L 20.8 15.6 L 19.0 19.4 L 16.5 25.5 L 15.0 31.3 L 14.1 37.1 L 14.1 39.0 L 14.5 40.9 L 15.1 42.3 L 15.8 43.4 L 16.8 44.5 L 18.1 45.5 L 19.3 47.0 L 21.0 48.3 L 23.0 49.1 L 26.2 49.7 L 43.6 49.7 L 43.8 49.2 L 43.9 47.4 L 43.9 44.6 L 43.8 42.8 L 43.6 42.3 L 26.4 42.3 L 22.2 42.5 L 21.1 42.5 L 21.0 42.3 L 21.0 41.1 L 22.6 33.4 L 23.7 29.9 L 25.2 25.9 L 27.2 21.5 L 29.5 17.2 L 32.1 13.1 L 34.9 9.2 L 34.7 8.6 L 33.2 7.1 Z" />
      </g>

      <template v-if="source">
        <circle
          cx="46"
          cy="15"
          r="7"
          fill="#6ed6ce"
          class="luce-mark-halo"
        />
        <circle
          cx="46"
          cy="15"
          r="4.4"
          :fill="`url(#${gradientId})`"
        />
      </template>
    </svg>
  </span>
</template>

<script setup>
import { computed, useId } from 'vue';

/**
 * La marque de Luce : sa capitale cursive, posée dans sa tuile.
 *
 * C'est le même tracé que la signature, simplement recadré — pas un second
 * dessin. La source lumineuse respire lentement, comme respiraient les points
 * de l'ancienne bulle : la pastille est présente sur tous les écrans du
 * périmètre interne, elle doit se remarquer sans happer le regard.
 */

const props = defineProps({
    /** Côté du carré, en pixels. */
    size: {
        type: Number,
        default: 26,
    },
    /** Passer à false pour figer la source (aperçus, captures, tests). */
    animated: {
        type: Boolean,
        default: true,
    },
    /** Sur le marine, la tuile s'éclaircit — sinon elle s'y noie. */
    onDark: {
        type: Boolean,
        default: false,
    },
});

// Sous 24 px la source ne vaut plus qu'un pixel : elle salit l'angle de la
// capitale au lieu de l'éclairer, on la retire.
const source = computed(() => props.size >= 24);
const tuile = computed(() => (props.onDark ? '#0d3a63' : '#02264a'));

// Deux marques sur le même écran partageraient l'identifiant du dégradé, et la
// seconde effacerait la première au démontage.
const gradientId = `luce-src-${useId()}`;
</script>

<style scoped>
.luce-mark {
  display: inline-flex;
  width: var(--taille);
  height: var(--taille);
  line-height: 0;
}

.luce-mark svg {
  width: 100%;
  height: 100%;
}

.luce-mark-halo {
  opacity: 0.35;
  transform-origin: 46px 15px;
  animation: luce-respire 2.4s ease-in-out infinite;
}

@keyframes luce-respire {
  0%, 100% {
    opacity: 0.28;
    transform: scale(1);
  }

  50% {
    opacity: 0.5;
    transform: scale(1.18);
  }
}

.luce-mark.is-static .luce-mark-halo {
  animation: none;
  opacity: 0.35;
}

/* Une animation en boucle permanente est un obstacle réel pour une partie des
   utilisateurs : on la coupe dès que le système le demande. */
@media (prefers-reduced-motion: reduce) {
  .luce-mark-halo {
    animation: none;
    opacity: 0.35;
  }
}
</style>
